import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button as SemanticButton, Input } from 'semantic-ui-react';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Button from "@mui/material/Button";
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import ChangePasswordForm from 'app/components/ChangePasswordForm';
import MessageModal from 'app/components/MessageModal';


import classes from 'app/components/Modal/classes.module.scss';
import 'semantic-ui-css/semantic.min.css'

import {
  getAccountUsers as getAccountUsersSvc,
  getAccountUser as getAccountUserSvc,
  becomeAccountUser as becomeAccountUserSvc,
  updateAccountPin as updateAccountPinSvc
} from 'app/services/accounts';

import {
  deleteAccountUser as deleteAccountUserSvc,
  createAccountUser as createAccountUserSvc,
  updateAccountUser as updateAccountUserSvc
} from 'app/services/members';

import {
  objectsAreSame
} from 'app/utils/valueFormatters';

import {
  login as loginPath
} from 'app/routes';

const AccountUserForm = (props) => {
  const { isOpen, onClose, account } = props;
  const [itemsChanged, setItemsChanged] = useState(false);
  const [data, setData] = useState([]);
  const [origData, setOrigData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [accountUser, setAccountUser] = useState(null);
  const [message, setMessage] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [error, setError] = useState(null);

  const history = useHistory();

  const roles = [
    { label: 'User', value: 'user' },
    { label: 'Admin', value: 'admin' },
    { label: 'Photographer', value: 'photographer' },
    { label: 'Service', value: 'service' },
  ];

  const roleValues = { "user": 0, "admin": 1, "photographer": 2, "service": 3 };

  let boxClass = classes.paper,
    boxStyle = { maxWidth: '800px' },
    headerVariant = 'h5', customPin = account.custom_pins;

  useEffect(() => {
    if (!loaded) {
      getAccountUsersSvc(account?.id).then((result) => {
        let accountUsers = [];
        let accountUserCalls = [];
        for (let accountUser of result.account_users) {
          accountUserCalls.push(
            getAccountUserSvc(accountUser.email, accountUser.id).then((user) => {
              accountUser.password = '';
              accountUser.pin = user.pin;
              accountUsers.push(accountUser);
            })
          );
        }

        Promise.all(accountUserCalls).then(() => {
          accountUsers.sort((a, b) => a.id - b.id);
          setData(accountUsers);
          setOrigData(accountUsers);
          setLoaded(true);
        });
      });
    }
  }, [loaded, isOpen])

  useEffect(() => {
    let isChanged = false;

    if (!objectsAreSame(origData, data)) {
      isChanged = true;
    }

    setItemsChanged(isChanged);
  }, [origData, data, isOpen])

  const handleDeleteRow = (rowIndex) => {
    const accountUsers = [...data];
    accountUsers.splice(rowIndex, 1);
    setData(accountUsers);
  }

  const handleBecomeRow = (rowIndex) => {
    let becomeUser = data[rowIndex];
    console.log(becomeUser);
    becomeAccountUserSvc(becomeUser.email, becomeUser.id).then(() => {
      history.push(loginPath);
    });
  }

  const onSubmit = async () => {
    let hasError = false;

    for (let item of data) {
      item.role = roleValues[item.role];
      if (item.id > 0) {
        if (item.touched) {
          await updateAccountUserSvc(account.id, item.id, item).catch((e) => {
            hasError = e.error !== null || e.error !== '';
            setError(e.error);
          });
        }
      } else {
        await createAccountUserSvc(account.id, item).catch((e) => {
          hasError = e.error !== null || e.error !== '';
          setError(e.error);
        });
      }
    }

    if (hasError) {
      return;
    }

    let forDeletes = [];
    let dataIds = data.map(d => { return d.id });
    for (let orig of origData) {
      if (orig.id && !dataIds.includes(orig.id)) {
        forDeletes.push(orig);
      }
    }

    for (let item of forDeletes) {
      await deleteAccountUserSvc(account.id, item.account_user_id);
    }

    setLoaded(false);
    onClose();
  }

  const deleteRowBody = (data, row) => {
    return <div>
      <IconButton aria-label="delete" onClick={() => handleDeleteRow(row.rowIndex)}>
        <DeleteIcon />
      </IconButton>
    </div>
  }

  const becomeRowBody = (data, row) => {
    if (data.id < 0) {
      return null
    }

    return <div>
      <IconButton aria-label="become" onClick={() => handleBecomeRow(row.rowIndex)}>
        <AccessibilityNewIcon />
      </IconButton>
    </div>
  }

  const onRowEditComplete = (e) => {
    let accountUsers = [...data];
    let { newData, index } = e;

    newData.touched = true;
    accountUsers[index] = newData;

    accountUsers.sort((a, b) => a.id - b.id);
    setData(accountUsers);
  };

  const fieldTextEditor = (options) => {
    let disabled = options.rowData.id > 0 && options.field === 'email';
    return (
      <div>
        <InputText value={options.value} onChange={(e) => options.editorCallback(e.target.value)} disabled={disabled} />
      </div>
    )
  }

  const fieldDropDownEditor = (options) => {
    // TODO: For some reason primereacts Dropdown input doesnt want to work
    // using muis Select for now -- same for DiscountForm component
    return (
      <Select
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}>
        {
          roles.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))
        }
      </Select>
    )
  }

  const passwordBodyTemplate = (rowData) => {
    if (!rowData.id) {
      return null
    }

    return (
      <Button variant="contained" onClick={() => {
        setAccountUser(rowData);
        setShowChangePassword(true);
      }} >Change</Button>
    )
  }

  const handleTriggerNewPin = (options) => {
    setAccountUser(options.rowData);
    if (confirm('This will replace your PIN with a new one. Would you like to proceed?') === true) {
      updateUserPin(options);
    }
  }

  const pinEditor = (options) => {
    if (customPin) {
      return <div>
        <Input size='mini' value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
      </div>
    }

    if (!options.rowData.id) {
      return null
    }

    return (
      <div>
        <Input size='mini' disabled value={options.value} action={
          <SemanticButton active onClick={() => {
            handleTriggerNewPin(options);
          }}>New Pin</SemanticButton>
        } />
      </div>
    )
  }

  const footerBody = () => {
    return <div>
      <Button variant="contained" onClick={handleAddRow}>Add</Button>
    </div>
  }

  const handleAddRow = () => {
    const accountUsers = [...data];
    accountUsers.push({ id: 0, email: '', role: 'user', password: '', pin: '' });
    setData(accountUsers);
  }

  const updateUserPin = (options) => {
    let params = { user_id: options.rowData.id }
    // if (customPin) {
    //   params.pin = pin;
    // }

    updateAccountPinSvc(account.id, params)
      .then((data) => {
        // Update the account pin in redux
        options.editorCallback(data.pin);
        setMessage('Approval PIN successfully updated ');
      }).catch(err => {
        setMessage(err.error);
      });
  }

  return (
    <div>
      <Modal open={isOpen} className={classes.modal} onClose={onClose}>
        <Box className={boxClass} style={boxStyle}>
          <CloseIcon className={classes.closeIcon} onClick={onClose} />

          <Typography variant={headerVariant} className={classes.formTitle}>
            Account Users
          </Typography>

          <DataTable key='id' value={data} editMode="row" dataKey="id" onRowEditComplete={onRowEditComplete} footer={footerBody} >
            <Column header='Email' field='email' editor={(options) => fieldTextEditor(options)} />
            <Column header='Role' field='role' editor={(options) => fieldDropDownEditor(options)} style={{ width: '20%' }} />
            <Column header='Password' field='password' body={passwordBodyTemplate} />
            <Column header='Pin' field='pin' editor={(options) => pinEditor(options)} />
            <Column rowEditor={true} headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
            <Column body={deleteRowBody} />
            <Column body={becomeRowBody} />
          </DataTable>

          <Button className={classes.button} variant="contained" onClick={onClose}>Cancel</Button>
          <Button className={classes.button} variant="contained" disabled={!itemsChanged} onClick={() => onSubmit()}>Save</Button>
        </Box>
      </Modal>
      <ChangePasswordForm accountUser={accountUser} isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} />
      <MessageModal
        isOpen={error !== null}
        onClose={() => setError(null)}
        title='Account Users'
        message={error}
      />
    </div>
  )
}

export default AccountUserForm;
